import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe, can } from "@/context/MeContext";
import { StatusBadge } from "@/components/StatusBadge";
import { InfoCard } from "@/components/record/Parts";
import { RecordList } from "@/components/record/RecordList";
import { fmtAgeMonths } from "@/lib/sp";
import { initials } from "@/lib/text";
import { PasswordPanel } from "@/components/PasswordPanel";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const DoctorCard = ({ p, editable, onStatus }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="doctor-profile-card">
    <div className="flex items-center gap-2">
      <h3 className="font-heading text-base font-bold text-stone-900">Profil médecin</h3>
      <StatusBadge value={p.affiliation_status || "PENDING"} testId="doctor-affiliation-status" />
    </div>
    <dl className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
      {[["Spécialité", p.specialty], ["N° d'inscription", p.license_number], ["Cabinet", p.work_address], ["Commune", p.cabinet_commune],
        ["Horaires", p.schedule ? `${(p.schedule.days || []).map((d) => DAYS[d]).join(", ")} · ${p.schedule.start}–${p.schedule.end}` : ""]].map(([k, v]) => (
        <div key={k}><dt className="text-stone-500">{k}</dt><dd className="text-stone-900">{v || "—"}</dd></div>
      ))}
    </dl>
    {editable && (
      <div className="flex flex-wrap gap-2 mt-5">
        <Button onClick={() => onStatus("APPROVED")} disabled={p.affiliation_status === "APPROVED"} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="doctor-approve-button"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Valider</Button>
        <Button variant="outline" onClick={() => onStatus("REJECTED")} disabled={p.affiliation_status === "REJECTED"} data-testid="doctor-reject-button"><XCircle className="h-4 w-4 mr-1.5" /> Refuser</Button>
      </div>
    )}
  </div>
);

export default function UserRecord() {
  const { id } = useParams();
  const api = useApi();
  const me = useMe();
  const [d, setD] = useState(null);
  const load = useCallback(() => api("get", `/sp/users/${id}/record`).then(setD).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-stone-500" data-testid="user-record-loading">Chargement…</p>;
  const u = d.user;

  const act = async (path, body, msg) => {
    try { await api("put", path, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="user-record-page">
      <Link to="/utilisateurs" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#C2410C]" data-testid="user-record-back"><ArrowLeft className="h-4 w-4" /> Utilisateurs</Link>
      <div className="bg-white border border-stone-200 rounded-lg p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="h-16 w-16 rounded-full bg-[#C2410C] text-white grid place-items-center font-heading text-xl font-black">{initials(u.full_name)}</div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-stone-900" data-testid="user-record-name">{u.full_name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <StatusBadge value={u.role} />
            <StatusBadge value={u.is_active === false ? "suspendu" : "actif"} testId="user-record-status" />
            <span className="text-xs text-stone-500 self-center">{u.is_verified ? "Compte vérifié" : "Compte non vérifié"}</span>
          </div>
        </div>
        {can(me, "write:users") && (
          <Button variant="outline" className="md:ml-auto" data-testid="user-toggle-active-button"
            onClick={() => act(`/sp/users/${u.id}`, { is_active: u.is_active === false }, u.is_active === false ? "Compte réactivé dans l'app" : "Compte suspendu dans l'app")}>
            {u.is_active === false ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Réactiver</> : <><Ban className="h-4 w-4 mr-1.5" /> Suspendre</>}
          </Button>
        )}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <InfoCard title="Coordonnées" testId="user-info" rows={[["E-mail", u.email], ["Téléphone", u.phone_number], ["Inscrit le", fmtDate(u.created_at?.slice(0, 10))], ["Mis à jour", fmtDate(u.updated_at?.slice(0, 10))]]} />
        <div className="lg:col-span-2 space-y-6">
          {can(me, "write:users") && <PasswordPanel user={u} />}
          {d.doctor_profile && <DoctorCard p={d.doctor_profile} editable={can(me, "write:doctors")} onStatus={(s) => act(`/sp/doctor_profiles/${d.doctor_profile.id}`, { affiliation_status: s }, "Affiliation mise à jour dans l'app")} />}
          {u.role === "PARENT" && (
            <RecordList title="Enfants" testId="user-children" items={d.children} empty="Aucun enfant enregistré."
              render={(p) => <Link to={`/enfants/${p.id}`} className="flex gap-4 hover:text-[#C2410C]" data-testid={`user-child-${p.id}`}><span className="font-medium">{p.full_name}</span><span className="text-stone-500">{fmtAgeMonths(p.age_months)} · {p.gender === "F" ? "Fille" : "Garçon"}</span></Link>} />
          )}
          <RecordList title="Rendez-vous" testId="user-appointments" items={d.appointments} empty="Aucun rendez-vous."
            render={(a) => <div className="flex flex-wrap gap-x-6 gap-y-1"><span className="font-medium w-40">{fmtDate(a.start_time?.slice(0, 16))}</span><span>{a.patient_name}</span><span className="text-stone-500">{a.doctor_name} · {a.reason || "—"}</span><span className="ml-auto"><StatusBadge value={a.status} /></span></div>} />
        </div>
      </div>
    </div>
  );
}
